import type { CompressionResult, ThumbnailResult } from '../types/photo'

export interface CompressionOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number
}

// Mirrors DEFAULT_PHOTO_UPLOAD_OPTIONS — see the note there on why 1600/0.8
// is the ceiling worth storing.
const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 1600,
  maxHeight: 1600,
  quality: 0.8,
}

export class ImageCompressionService {
  /**
   * Compress an image file
   */
  async compress(file: File, options: CompressionOptions = {}): Promise<CompressionResult> {
    // Drop undefined entries before merging: a partial options object would
    // otherwise overwrite a default with undefined, which skips the resize
    // entirely and hands canvas.toBlob no quality (browser default 0.92) —
    // a full-resolution capture straight into the bucket.
    const opts: Required<CompressionOptions> = {
      ...DEFAULT_OPTIONS,
      ...Object.fromEntries(Object.entries(options).filter(([, v]) => v !== undefined)),
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onerror = () => reject(new Error('Failed to read file'))

      reader.onload = (e) => {
        const img = new Image()

        img.onerror = () => reject(new Error('Failed to load image'))

        img.onload = () => {
          try {
            // Calculate new dimensions
            let { width, height } = img
            const aspectRatio = width / height

            if (width > opts.maxWidth) {
              width = opts.maxWidth
              height = width / aspectRatio
            }

            if (height > opts.maxHeight) {
              height = opts.maxHeight
              width = height * aspectRatio
            }

            // Create canvas and draw resized image
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height

            const ctx = canvas.getContext('2d')
            if (!ctx) {
              reject(new Error('Failed to get canvas context'))
              return
            }

            ctx.drawImage(img, 0, 0, width, height)

            // Convert canvas to blob
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to create blob'))
                  return
                }

                // Create File from Blob
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                })

                const compressionRatio = file.size / blob.size

                resolve({
                  compressed_file: compressedFile,
                  original_size: file.size,
                  compressed_size: blob.size,
                  compression_ratio: compressionRatio,
                })
              },
              'image/jpeg',
              opts.quality
            )
          } catch (error) {
            reject(error instanceof Error ? error : new Error(String(error)))
          }
        }

        img.src = e.target?.result as string
      }

      reader.readAsDataURL(file)
    })
  }

  /**
   * Generate thumbnail from image
   */
  async generateThumbnail(file: File, size: number = 200): Promise<ThumbnailResult> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onerror = () => reject(new Error('Failed to read file'))

      reader.onload = (e) => {
        const img = new Image()

        img.onerror = () => reject(new Error('Failed to load image'))

        img.onload = () => {
          try {
            // Calculate thumbnail dimensions (square crop from center)
            const { width: origWidth, height: origHeight } = img
            const minDim = Math.min(origWidth, origHeight)

            // Source coordinates for center crop
            const sx = (origWidth - minDim) / 2
            const sy = (origHeight - minDim) / 2

            // Create canvas for thumbnail
            const canvas = document.createElement('canvas')
            canvas.width = size
            canvas.height = size

            const ctx = canvas.getContext('2d')
            if (!ctx) {
              reject(new Error('Failed to get canvas context'))
              return
            }

            // Draw cropped and resized image
            ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size)

            // Convert canvas to blob
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to create thumbnail blob'))
                  return
                }

                const thumbnailUrl = URL.createObjectURL(blob)

                resolve({
                  thumbnail_blob: blob,
                  thumbnail_url: thumbnailUrl,
                  width: size,
                  height: size,
                })
              },
              'image/jpeg',
              0.8
            )
          } catch (error) {
            reject(error instanceof Error ? error : new Error(String(error)))
          }
        }

        img.src = e.target?.result as string
      }

      reader.readAsDataURL(file)
    })
  }

  /**
   * Get image dimensions without loading full image
   */
  async getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onerror = () => reject(new Error('Failed to read file'))

      reader.onload = (e) => {
        const img = new Image()

        img.onerror = () => reject(new Error('Failed to load image'))

        img.onload = () => {
          resolve({
            width: img.width,
            height: img.height,
          })
        }

        img.src = e.target?.result as string
      }

      reader.readAsDataURL(file)
    })
  }

  /**
   * Check if image needs compression
   */
  needsCompression(
    fileSize: number,
    maxSize: number = 2 * 1024 * 1024 // 2MB default
  ): boolean {
    return fileSize > maxSize
  }

  /**
   * Validate image file
   */
  isValidImage(file: File): boolean {
    return file.type.startsWith('image/')
  }
}

// Export singleton instance
export const imageCompressionService = new ImageCompressionService()
